import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useSelector, useDispatch } from "react-redux";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Chip,
  Grid,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import {
  bulkUpdateProperties,
  deleteProperties,
  updateProperty,
} from "../services/propertyService";
import { MARKER_OPTIONS, markerOptionsWithLabels } from "../markerColors";
import { loadPropertyIntoAssessor } from "../services/assessorLoad";
import {
  resetForm,
  setAssessorValue,
  setBuildingAddr,
  setCIDValue,
  setContacts,
  setData,
  setOwnerMailing,
  setOwnerName,
  setParcelID,
  setPropertyDesc,
  setExempt,
  setMarker,
  setCustomFields,
} from "../features/assessorSlice";
import { countActiveFilters } from "./filterPanel";
import { canWriteCid } from "../utils/permissions";
import { getCIDProperties, getCIDContacts } from "../services/cidService";
import { joinContactsToProperties } from "../utils/propertyJoin";
import { resetContacts } from "../features/contactSlice";
import { ImportExport, Edit } from "@mui/icons-material";
import { persistSelectedProperty } from "../utils/assessorPersistence";

// Display-only currency formatter for the value columns ("$1,234.56"). Applied
// via valueFormatter so the cell editors still receive the raw number.
const currencyFormatter = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return "";
  }
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

// Serializes a Date from the grid's date editor back to the "YYYY-MM-DD"
// string shape custom fields are stored in.
const toDateString = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return "";
  }
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${value.getFullYear()}-${month}-${day}`;
};

// valueSetter for cf_* columns: writes the edited value back into the row's
// custom_fields object (deleting empty values, matching setCustomFieldValue).
const customFieldSetter = (name, transform = (v) => v) => (rawValue, row) => {
  const next = { ...(row.custom_fields || {}) };
  const value = transform(rawValue);
  if (
    value === "" ||
    value === null ||
    value === undefined ||
    (typeof value === "number" && Number.isNaN(value))
  ) {
    delete next[name];
  } else {
    next[name] = value;
  }
  return { ...row, custom_fields: next };
};

const PropertyTable = (props) => {
  const [selected, setSelected] = useState([]);
  const [tableSearch, setTableSearch] = useState("");
  // Bulk edit mode: multiselect only exists inside this explicit mode, so
  // ordinary row clicks keep their single-select "open in editor" meaning.
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelection, setBulkSelection] = useState([]);
  const [bulkField, setBulkField] = useState("flag_color");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const navigate = useNavigate();
  const gridWrapRef = useRef(null);
  const [gridHeight, setGridHeight] = useState(560);
  const cid = useSelector((state) => state.assessor.value.form.cid);
  const data = useSelector((state) => state.assessor.value.data);
  const filters = useSelector((state) => state.assessor.value.filters);
  const mils = useSelector((state) => state.assessor.value.mils);
  const legend = useSelector((state) => state.cid.value.legend);
  const auth = useSelector((state) => state.auth.value);
  const cidWritable = canWriteCid(auth, cid);
  const editorTransferID = useSelector(
    (state) => state.assessor.value.form.transferID
  );
  const propertyFieldDefs = useSelector((state) => state.settings.value.propertyFields);
  const contactFieldDefs = useSelector((state) => state.settings.value.contactFields);
  const propertyFields = useMemo(
    () => propertyFieldDefs.filter((f) => !f.archived),
    [propertyFieldDefs]
  );
  const contactFields = useMemo(
    () => contactFieldDefs.filter((f) => !f.archived),
    [contactFieldDefs]
  );
  const activeFieldNames = useMemo(
    () => new Set(propertyFields.map((f) => f.internal_name)),
    [propertyFields]
  );
  const dispatch = useDispatch();

  // Reflect the property currently loaded in the editor as the (single) grid
  // selection — this keeps the checkbox in sync with row clicks, refresh
  // rehydration, and "Open in Quick Assessor" alike.
  useEffect(() => {
    setSelected(
      editorTransferID && editorTransferID !== -1 ? [editorTransferID] : []
    );
  }, [editorTransferID]);

  // Stretch the grid so its bottom edge always meets the bottom of the window,
  // whatever the viewport height. Measured (not a magic constant) so it stays
  // correct as the header above it changes.
  const recomputeHeight = useCallback(() => {
    const el = gridWrapRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    // setState is a no-op when the value is unchanged, so re-measuring on every
    // render (below) is cheap and won't loop.
    setGridHeight(Math.max(360, window.innerHeight - top - 24));
  }, []);

  // Re-measure after every commit (data load, header wrap, editor toggle) and
  // whenever the window resizes.
  useLayoutEffect(() => {
    recomputeHeight();
  });
  useEffect(() => {
    window.addEventListener("resize", recomputeHeight);
    return () => window.removeEventListener("resize", recomputeHeight);
  }, [recomputeHeight]);

  const selectedId = selected.length ? selected[selected.length - 1] : null;

  const activeFilterCount = countActiveFilters(filters, activeFieldNames);

  const filteredRows = useMemo(() => {
    const search = tableSearch.trim().toLowerCase();

    return data.filter((row) => {
      if (search) {
        const fields = [
          row.parcelID,
          row.owner_name,
          row.owner_mailing,
          row.building_address,
          row.property_desc,
          row.name,
          row.company,
          row.phone_number,
          ...Object.values(row.custom_fields || {}),
          ...Object.values(row.contact_custom_fields || {}),
        ];
        const hit = fields.some((value) =>
          String(value ?? "").toLowerCase().includes(search)
        );
        if (!hit) {
          return false;
        }
      }

      if (filters.markers.length) {
        const marker = row.flag_color || "none";
        if (!filters.markers.includes(marker)) {
          return false;
        }
      }

      for (const [name, filter] of Object.entries(filters.custom)) {
        if (!activeFieldNames.has(name)) {
          continue; // stale filter on an archived/removed field — ignore
        }
        const value = row.custom_fields?.[name];
        if (filter.q !== undefined) {
          if (!String(value ?? "").toLowerCase().includes(filter.q.toLowerCase())) {
            return false;
          }
        }
        if (filter.min !== undefined || filter.max !== undefined) {
          const num = typeof value === "number" ? value : parseFloat(value);
          if (!Number.isFinite(num)) return false;
          if (filter.min !== undefined && num < filter.min) return false;
          if (filter.max !== undefined && num > filter.max) return false;
        }
        if (filter.from !== undefined || filter.to !== undefined) {
          const date = String(value || "");
          if (!date) return false;
          if (filter.from !== undefined && date < filter.from) return false;
          if (filter.to !== undefined && date > filter.to) return false;
        }
        if (filter.selected !== undefined) {
          if (!filter.selected.includes(String(value ?? ""))) return false;
        }
      }

      return true;
    });
  }, [data, tableSearch, filters, activeFieldNames]);

  const columns = useMemo(() => [
    {
      field: "flag_color",
      width: 110,
      headerName: "Mark",
      editable: true,
      type: "singleSelect",
      valueOptions: [
        { value: "", label: "No marker" },
        ...markerOptionsWithLabels(legend).map(({ value, label }) => ({
          value,
          label,
        })),
      ],
      valueGetter: (value) => value || "",
      valueSetter: (value, row) => ({
        ...row,
        flag_color: value || null,
      }),
      // The marker color washes over the whole row (getRowClassName below),
      // so the cell just names the marker per the CID's legend. When the CID
      // hasn't labeled this color, leave the cell blank rather than showing a
      // generic "Red Marker"/"Purple Marker" fallback — the row color already
      // conveys the marker.
      renderCell: (params) => {
        if (!params.value) {
          return null;
        }
        const option = MARKER_OPTIONS.find((o) => o.value === params.value);
        const legendLabel = legend?.[option?.legendIndex];
        if (!legendLabel || !legendLabel.trim()) {
          return null;
        }
        return (
          <Typography
            variant="body2"
            noWrap
            sx={{
              color: option?.color,
              fontWeight: 600,
              // Center within the cell like the grid's own text content.
              height: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            {legendLabel}
          </Typography>
        );
      },
    },
    {
      field: "last_updated",
      headerName: "Last Updated",
      type: "date",
      width: 100,
      valueGetter: (value) => (value ? new Date(value) : null),
    },
    { field: "parcelID", headerName: "Parcel ID", width: 150, editable: true },
    { field: "owner_name", headerName: "Owner Name", width: 150, editable: true },
    { field: "owner_mailing", headerName: "Mailing Address", width: 150, editable: true },
    { field: "building_address", headerName: "Building Address", width: 150, editable: true },
    {
      field: "assessor_value",
      headerName: "Assessor Value",
      width: 150,
      type: "number",
      editable: true,
      valueGetter: (value) =>
        value === null || value === undefined || value === ""
          ? null
          : Number(value),
      valueFormatter: currencyFormatter,
    },
    {
      field: "cid_value",
      headerName: "CID Value",
      width: 100,
      type: "number",
      editable: true,
      valueGetter: (value) =>
        value === null || value === undefined || value === ""
          ? null
          : Number(value),
      valueFormatter: currencyFormatter,
    },
    { field: "property_desc", headerName: "Property Desc", width: 150, editable: true },

    { field: "name", headerName: "Name", width: 150 },
    { field: "phone_number", headerName: "Phone Number", width: 150 },
    { field: "email", headerName: "Email", width: 150 },
    { field: "company", headerName: "Company", width: 150 },
    { field: "secondary_contact", headerName: "Secondary Contact", width: 150 },
    { field: "secondary_phone", headerName: "Secondary Phone", width: 150 },
    { field: "secondary_email", headerName: "Secondary Email", width: 150 },
    { field: "notes", headerName: "Notes", width: 150 },
    { field: "callsMade", headerName: "Calls Made", width: 150, valueGetter: (value) => value ? value : 0 },
    { field: "followUpCalls", headerName: "Follow Up Calls", width: 150, valueGetter: (value) => value ? value : 0 },
    { field: "emailsSent", headerName: "Emails Sent", width: 150, valueGetter: (value) => value ? value : 0 },
    { field: "dropIns", headerName: "Drops In", width: 150, valueGetter: (value) => value ? value : 0 },

    ...propertyFields.map((f) => {
      const base = {
        field: `cf_${f.internal_name}`,
        headerName: f.label,
        width: 150,
        editable: true,
      };
      switch (f.field_type) {
        case "number":
          return {
            ...base,
            type: "number",
            valueGetter: (value, row) => {
              const stored = row.custom_fields?.[f.internal_name];
              return stored === undefined || stored === "" ? null : Number(stored);
            },
            valueSetter: customFieldSetter(f.internal_name),
          };
        case "date":
          return {
            ...base,
            type: "date",
            valueGetter: (value, row) => {
              const stored = row.custom_fields?.[f.internal_name];
              return stored ? new Date(`${stored}T00:00:00`) : null;
            },
            valueSetter: customFieldSetter(f.internal_name, toDateString),
          };
        case "select":
          return {
            ...base,
            type: "singleSelect",
            valueOptions: [
              { value: "", label: "—" },
              ...(f.options || []).map((option) => ({ value: option, label: option })),
            ],
            valueGetter: (value, row) =>
              row.custom_fields?.[f.internal_name] ?? "",
            valueSetter: customFieldSetter(f.internal_name),
          };
        default:
          return {
            ...base,
            valueGetter: (value, row) =>
              row.custom_fields?.[f.internal_name] ?? "",
            valueSetter: customFieldSetter(f.internal_name),
          };
      }
    }),
    ...contactFields.map((f) => ({
      field: `ccf_${f.internal_name}`,
      headerName: `${f.label} (Contact)`,
      width: 150,
      valueGetter: (value, row) => row.contact_custom_fields?.[f.internal_name] ?? "",
    })),
  ], [propertyFields, contactFields, legend]);

  const processRowUpdate = useCallback(
    async (newRow, oldRow) => {
      // Preserve blank/null monetary values — coercing them to 0 on an
      // unrelated cell edit would silently overwrite imported rows (and a
      // cid_value of 0 reads as "exempt").
      const toNumberOrNull = (value) => {
        if (value === null || value === undefined || value === "") {
          return null;
        }
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
      };
      const assessorValue = toNumberOrNull(newRow.assessor_value);
      let cidValue = toNumberOrNull(newRow.cid_value);
      const oldAssessor = toNumberOrNull(oldRow.assessor_value);
      const oldCid = toNumberOrNull(oldRow.cid_value);

      // Mirror the editor's mils math: editing the assessor value recomputes
      // the CID value, unless the row is exempt (cid_value 0) or the user
      // edited the CID value directly.
      const assessorChanged = assessorValue !== oldAssessor;
      const cidChanged = cidValue !== oldCid;

      // The grid calls processRowUpdate on every committed cell edit, even when
      // the value is unchanged (e.g. entering and leaving an editor). Skip the
      // save — and the "Updated Property!" snackbar — when nothing actually
      // changed across the editable columns.
      const textChanged =
        newRow.parcelID !== oldRow.parcelID ||
        newRow.owner_name !== oldRow.owner_name ||
        newRow.owner_mailing !== oldRow.owner_mailing ||
        newRow.building_address !== oldRow.building_address ||
        newRow.property_desc !== oldRow.property_desc ||
        (newRow.flag_color || null) !== (oldRow.flag_color || null);
      const customFieldsChanged =
        JSON.stringify(newRow.custom_fields || {}) !==
        JSON.stringify(oldRow.custom_fields || {});
      if (!textChanged && !assessorChanged && !cidChanged && !customFieldsChanged) {
        return oldRow;
      }

      if (
        assessorChanged &&
        !cidChanged &&
        assessorValue !== null &&
        oldCid !== 0
      ) {
        cidValue = assessorValue * (parseFloat(mils) || 0);
      }

      const payload = {
        transferID: newRow.id,
        parcelID: newRow.parcelID,
        ownerName: newRow.owner_name,
        ownerMailing: newRow.owner_mailing,
        buildingAddress: newRow.building_address,
        assessorValue,
        cidValue,
        propertyDesc: newRow.property_desc,
        flagColor: newRow.flag_color || null,
        customFields: newRow.custom_fields || {},
        // Pass the row's contacts through so the save doesn't clear them.
        contact_ids: Array.isArray(newRow.contact_ids)
          ? newRow.contact_ids
          : newRow.contact_id
            ? [newRow.contact_id]
            : [],
      };

      // Throws on failure — the grid reverts the cell.
      const updated = await updateProperty(payload);
      const merged = { ...newRow, ...updated };
      dispatch(setData(data.map((row) => (row.id === merged.id ? merged : row))));

      // Keep the side editor in sync when it has this property open.
      if (editorTransferID === merged.id) {
        dispatch(setParcelID(merged.parcelID));
        dispatch(setOwnerName(merged.owner_name));
        dispatch(setOwnerMailing(merged.owner_mailing));
        dispatch(setMarker(merged.flag_color));
        dispatch(setCustomFields(merged.custom_fields || {}));
        dispatch(setBuildingAddr(merged.building_address));
        dispatch(setPropertyDesc(merged.property_desc));
        dispatch(setExempt(parseFloat(merged.cid_value) === 0));
        dispatch(setAssessorValue(merged.assessor_value));
        dispatch(setCIDValue(merged.cid_value));
      }

      return merged;
    },
    [data, dispatch, editorTransferID, mils]
  );

  const handleProcessRowUpdateError = useCallback((err) => {
    // updateProperty already surfaced a snackbar; just log for debugging.
    console.log(err);
  }, []);

  const handleSelect = async (model) => {
    props.toggleLookup(true);
    // Enforce single selection: keep only the most recently clicked row so a
    // second checkbox click replaces (rather than adds to) the selection.
    const next = model.length ? [model[model.length - 1]] : [];
    setSelected(next);
    if (next.length) {
      await loadPropertyIntoAssessor(dispatch, next[0]);
    } else {
      persistSelectedProperty(null);
      dispatch(resetForm());
      dispatch(resetContacts());
      dispatch(setContacts([]));
    }
  };

  // Switching CIDs invalidates any bulk selection — drop out of the mode.
  useEffect(() => {
    setBulkMode(false);
    setBulkSelection([]);
    setBulkValue("");
  }, [cid]);

  // The fields bulk edit may change: every property-owned column except
  // Parcel ID (the row identity). Contact columns are shared records — bulk
  // writing them would edit contacts attached to other properties too.
  const bulkFieldOptions = useMemo(
    () => [
      { key: "flag_color", label: "Marker", type: "marker" },
      { key: "owner_name", label: "Owner Name", type: "text" },
      { key: "owner_mailing", label: "Mailing Address", type: "text" },
      { key: "building_address", label: "Building Address", type: "text" },
      { key: "assessor_value", label: "Assessor Value", type: "number" },
      { key: "cid_value", label: "CID Value", type: "number" },
      { key: "property_desc", label: "Property Desc", type: "text" },
      ...propertyFields.map((f) => ({
        key: `cf:${f.internal_name}`,
        label: f.label,
        type: f.field_type,
        options: f.options,
        internalName: f.internal_name,
      })),
    ],
    [propertyFields]
  );
  const activeBulkField =
    bulkFieldOptions.find((o) => o.key === bulkField) || bulkFieldOptions[0];

  // Excel-style row highlighting: each marked row gets a low-opacity wash of
  // its marker color, deepening on hover/selection. Selectors carry the
  // .MuiDataGrid-row class so they outrank the grid's own hover/selected
  // backgrounds.
  const markerRowStyles = useMemo(() => {
    const styles = {};
    MARKER_OPTIONS.forEach(({ value, color }) => {
      styles[`& .MuiDataGrid-row.marker-row--${value}`] = {
        backgroundColor: alpha(color, 0.14),
      };
      styles[`& .MuiDataGrid-row.marker-row--${value}:hover`] = {
        backgroundColor: alpha(color, 0.22),
      };
      styles[`& .MuiDataGrid-row.marker-row--${value}.Mui-selected`] = {
        backgroundColor: alpha(color, 0.3),
      };
      styles[`& .MuiDataGrid-row.marker-row--${value}.Mui-selected:hover`] = {
        backgroundColor: alpha(color, 0.34),
      };
    });
    return styles;
  }, []);

  // Re-fetch this CID's rows with contacts joined (same shape the CID load
  // produces) — used after bulk mutations that touch many rows at once.
  const refreshRows = useCallback(async () => {
    const [refreshProperties, resContacts] = await Promise.all([
      getCIDProperties(cid),
      getCIDContacts(cid),
    ]);
    dispatch(
      setData(joinContactsToProperties(refreshProperties?.data, resContacts))
    );
  }, [cid, dispatch]);

  const applyBulk = async () => {
    setBulkBusy(true);
    try {
      let changes;
      switch (activeBulkField.key) {
        case "flag_color":
          changes = { flagColor: bulkValue || null };
          break;
        case "owner_name":
          changes = { ownerName: bulkValue };
          break;
        case "owner_mailing":
          changes = { ownerMailing: bulkValue };
          break;
        case "building_address":
          changes = { buildingAddress: bulkValue };
          break;
        case "assessor_value":
          changes = { assessorValue: bulkValue };
          break;
        case "cid_value":
          changes = { cidValue: bulkValue };
          break;
        case "property_desc":
          changes = { propertyDesc: bulkValue };
          break;
        default:
          changes = { customFields: { [activeBulkField.internalName]: bulkValue } };
      }
      await bulkUpdateProperties(bulkSelection, changes);
      await refreshRows();
      // The side editor may have one of the changed rows open — resync it.
      if (bulkSelection.includes(editorTransferID)) {
        await loadPropertyIntoAssessor(dispatch, editorTransferID);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleteOpen(false);
    setBulkBusy(true);
    try {
      await deleteProperties(bulkSelection);
      await refreshRows();
      if (bulkSelection.includes(editorTransferID)) {
        persistSelectedProperty(null);
        dispatch(resetForm());
        dispatch(resetContacts());
        dispatch(setContacts([]));
      }
      setBulkSelection([]);
    } finally {
      setBulkBusy(false);
    }
  };

  const exitBulkMode = () => {
    setBulkMode(false);
    setBulkSelection([]);
    setBulkValue("");
  };

  const bulkValueInput = () => {
    switch (activeBulkField.type) {
      case "marker":
        return (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>New value</InputLabel>
            <Select
              label="New value"
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
            >
              <MenuItem value="">
                <em>No marker</em>
              </MenuItem>
              {markerOptionsWithLabels(legend).map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "select":
        return (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>New value</InputLabel>
            <Select
              label="New value"
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
            >
              <MenuItem value="">
                <em>— Clear —</em>
              </MenuItem>
              {(activeBulkField.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "number":
        return (
          <TextField
            size="small"
            type="number"
            label="New value"
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
          />
        );
      case "date":
        return (
          <TextField
            size="small"
            type="date"
            label="New value"
            InputLabelProps={{ shrink: true }}
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
          />
        );
      default:
        return (
          <TextField
            size="small"
            label="New value"
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
          />
        );
    }
  };

  return (
    <Grid container mb={2} spacing={1}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent sx={{ py: 1.5 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6">
                  Property Results ({filteredRows.length}/{data.length})
                </Typography>
                {activeFilterCount > 0 ? (
                  <Chip
                    size="small"
                    color="primary"
                    label={`${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""} on`}
                  />
                ) : null}
              </Stack>
              <TextField
                size="small"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                label="Filter rows"
                placeholder="Parcel, owner, address, contact..."
                sx={{ minWidth: { xs: "100%", md: 320 } }}
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {bulkMode ? (
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderColor: "primary.main" }}>
            <CardContent sx={{ py: 1.5 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", md: "center" }}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  color="primary"
                  label={`Bulk edit — ${bulkSelection.length} selected`}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Field</InputLabel>
                  <Select
                    label="Field"
                    value={activeBulkField.key}
                    onChange={(e) => {
                      setBulkField(e.target.value);
                      setBulkValue("");
                    }}
                  >
                    {bulkFieldOptions.map((o) => (
                      <MenuItem key={o.key} value={o.key}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {bulkValueInput()}
                <Button
                  variant="contained"
                  disabled={!bulkSelection.length || bulkBusy}
                  onClick={applyBulk}
                >
                  Apply to {bulkSelection.length}
                </Button>
                <Button
                  color="error"
                  variant="outlined"
                  disabled={!bulkSelection.length || bulkBusy}
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  Delete Selected
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {activeBulkField.key === "assessor_value"
                    ? "CID Value recomputes from mils for non-exempt rows. A blank value clears the field."
                    : "Tick rows (or the header checkbox for all), pick a field, and apply. A blank value clears the field."}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ) : null}

      <Grid item xs={12} ref={gridWrapRef} sx={{ height: gridHeight }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSizeOptions={[25, 50, 100]}
          checkboxSelection
          disableMultipleRowSelection={!bulkMode}
          rowSelectionModel={bulkMode ? bulkSelection : selected}
          onRowSelectionModelChange={(p) =>
            bulkMode ? setBulkSelection(p) : handleSelect(p)
          }
          disableRowSelectionOnClick
          isCellEditable={() => cidWritable && !bulkMode}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          getRowClassName={(params) =>
            params.row.flag_color ? `marker-row--${params.row.flag_color}` : ""
          }
          // jsdom has no layout, so virtualization would render zero rows in
          // tests (MUI's documented testing escape hatch).
          disableVirtualization={process.env.NODE_ENV === "test"}
          sx={{
            backgroundColor: "white",
            borderRadius: 1,
            ...markerRowStyles,
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: "last_updated", sort: "desc" }],
            },
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          slots={{
            toolbar: () => {
              if (!cid) {
                return null;
              }
              return bulkMode ? (
                <GridToolbarContainer>
                  <Button variant="contained" size="small" onClick={exitBulkMode}>
                    Exit Bulk Edit
                  </Button>
                  <GridToolbarExport />
                </GridToolbarContainer>
              ) : (
                <GridToolbarContainer>
                  <Button
                    color="success"
                    startIcon={<ImportExport />}
                    onClick={() => navigate("/app/import")}
                  >
                    Import
                  </Button>
                  <GridToolbarExport />
                  <Button
                    startIcon={<Edit />}
                    disabled={!cidWritable}
                    onClick={() => {
                      setBulkMode(true);
                      setBulkSelection([]);
                    }}
                  >
                    Bulk Edit
                  </Button>
                  <Button
                    color="error"
                    disabled={!cidWritable || selectedId === null}
                    onClick={async () => {
                      deleteProperties(selected).then(async () => {
                        const refreshProperties = await getCIDProperties(cid);
                        dispatch(setData(refreshProperties.data));
                        // Drop the deleted rows from selection and the editor so
                        // nothing dangles.
                        setSelected([]);
                        persistSelectedProperty(null);
                      });
                    }}
                  >
                    Delete Row
                  </Button>
                </GridToolbarContainer>
              );
            },
          }}
        />
      </Grid>

      <Dialog
        fullWidth
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
      >
        <DialogTitle>
          Delete {bulkSelection.length} selected propert
          {bulkSelection.length === 1 ? "y" : "ies"}? This can't be undone.
        </DialogTitle>
        <DialogActions>
          <Button variant="outlined" onClick={() => setBulkDeleteOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleBulkDelete}>
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default PropertyTable;
